// Hooks
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

// Contentful
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

// GSAP
import { gsap, Power3 } from 'gsap';
import { SplitText } from '../../libs/SplitText';

// Components
import RequestInvite from '../RequestInvite/RequestInvite';
import NewsletterSubscribe from '../NewsletterSubscribe/NewsletterSubscribe';

const CategoryHeader = (props) => {
  const { title, highlight, bodyText, buttonLabel, buttonAction } = props;

  const refTitle = useRef();
  const refBodyText = useRef();

  const [categoryHeader, setCategoryHeader] = useState('');
  const categoryHeaderInnerHTML = { __html: categoryHeader };

  // intersection observer
  const [titleContainer, inView] = useInView({
    threshold: 0,
    rootMargin: '-225px 0px 0px 0px',
  });

  useEffect(() => {
    let tempString = '';

    if (highlight === '' || title.indexOf(highlight) === -1) {
      // use just the title if highlight is empty or does not occur in title
      tempString = title;
    } else {
      // split title by highlight
      const titleRest = title.split(highlight);

      if (titleRest[0] === '') {
        // highlight at start of title
        tempString = `<span class="CategoryHeader-titleHighlight">${highlight}</span>${titleRest[1]}`;
      } else if (titleRest[1] === '') {
        // highlight at end of title
        tempString = `${titleRest[0]}<span class="CategoryHeader-titleHighlight">${highlight}</span>`;
      } else {
        // highlight in middle of title
        tempString = `${titleRest[0]}<span class="CategoryHeader-titleHighlight">${highlight}</span>${titleRest[1]}`;
      }
    }
    setCategoryHeader(tempString);
  }, []);

  useEffect(() => {
    if (categoryHeader !== '') {
      const splitWords = new SplitText(refTitle.current, {
        type: 'words',
      });
      const tl = gsap.timeline({ paused: true });

      tl.from(splitWords.words, {
        duration: 0.5,
        delay: 0.15,
        opacity: 0,
        scale: 1,
        x: 0,
        y: 55,
        stagger: 0.05,
        ease: Power3.easeOut,
      });
      if (bodyText !== null) {
        tl.to(refBodyText.current, { duration: 0.5, opacity: 1, y: 0, ease: Power3.easeOut }, '-=85%');
      }

      // show title
      refTitle.current.classList.add('is-visible');

      // play title intro animation
      tl.play();
    }
  }, [categoryHeader]);

  useEffect(() => {
    // scroll color change
    if (inView) {
      refTitle.current.classList.add('is-focused');
    } else {
      refTitle.current.classList.remove('is-focused');
    }
  }, [inView]);

  return (
    <div className="CategoryHeader CategoryHeader-transparent">
      <div className="CategoryHeader-titleContainer" ref={titleContainer}>
        <h1 className="CategoryHeader-title" ref={refTitle} dangerouslySetInnerHTML={categoryHeaderInnerHTML} />
      </div>

      {bodyText && (
        <div className="CategoryHeader-body" ref={refBodyText}>
          {documentToReactComponents(bodyText)}
        </div>
      )}

      {buttonAction === 'requestInvite' && (
        <RequestInvite
          name="home-page-footer"
          defaultLabel="you@email.com"
          buttonLabel={buttonLabel}
          buttonURL="/request-invite"
          className="RequestInvite--electricPurple"
          modifier="RequestInvite--electricPurple"
          source="category-header"
        />
      )}
      {buttonAction === 'subscribe' && (
        <>
          <NewsletterSubscribe
            defaultLabel="you@email.com"
            buttonLabel={buttonLabel}
            className="RequestInvite--electricPurple"
            modifier="NewsletterSubscribe--electricPurple"
          />
        </>
      )}
    </div>
  );
};

export default CategoryHeader;
